<template>
    <component :is="page ? 'div' : 'a-modal'" v-bind="page ? {} : modalProps" @cancel="$emit('close')">
        <div class="phm-modal" :class="{ 'phm-page': page }">
            <div class="phm-head" v-if="!page">
                <div>
                    <h3>Histórico do produto</h3>
                    <div class="phm-sub">
                        {{ product.pro_descricao }}<template v-if="brand && brand.pro_observacao"> · {{ brand.pro_observacao }}</template>
                    </div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="phm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <a-tabs v-model="tab" class="phm-tabs" @change="onTabChange">
                <a-tab-pane key="offers" tab="Histórico de digitação" />
                <a-tab-pane key="requests" tab="Histórico de requisição" />
            </a-tabs>

            <div v-if="tab === 'offers'" class="phm-tools">
                <a-range-picker
                    v-model="rangeDate"
                    class="phm-range"
                    format="DD/MM/YYYY"
                    :locale="ptBR"
                    :placeholder="['Início', 'Fim']"
                />
                <a-select
                    v-if="!brand"
                    v-model="selectedBrandId"
                    class="phm-brand"
                    show-search
                    allowClear
                    placeholder="Marca"
                    :options="brandOptions"
                    :filterOption="filterOption"
                    @change="loadOffers(1)"
                />
                <a-button icon="search" @click="loadOffers(1)">Filtrar</a-button>
            </div>

            <div class="phm-body">
                <a-table
                    v-if="tab === 'offers'"
                    size="small"
                    :columns="offerColumns"
                    :data-source="offers"
                    :row-key="(record, index) => index"
                    :loading="loading"
                    :pagination="offersPagination"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Sem registro.' }"
                    @change="(p) => loadOffers(p.current)"
                >
                    <template slot="request" slot-scope="text, log">{{ log.request ? log.request.id : "--" }}</template>
                    <template slot="provider" slot-scope="text, log">
                        <div class="phm-strong">{{ log.provider ? log.provider.trading_name : "--" }}</div>
                        <div class="phm-muted">{{ log.seller ? log.seller.name : "" }}</div>
                    </template>
                    <template slot="billing" slot-scope="text, log">
                        <span class="phm-money">{{ log.delivery ? $options.filters.currency(log.delivery.minimum_billing) : "--" }}</span>
                    </template>
                    <template slot="price" slot-scope="text, log">
                        <a-tooltip :title="log.purchased ? 'Este item foi comprado' : ''">
                            <span class="phm-money" :class="{ 'phm-purchased': log.purchased }">{{ log.price | currency }}</span>
                        </a-tooltip>
                    </template>
                    <template slot="brand" slot-scope="text, log">{{ (log.product && log.product.brand) || "--" }}</template>
                    <template slot="date" slot-scope="text, log">{{ log.request ? log.request.created_at : null | formatDateOnly }}</template>
                    <template slot="obs" slot-scope="text">
                        <span class="phm-obs" :title="text">{{ text || "--" }}</span>
                    </template>
                </a-table>

                <a-table
                    v-else
                    size="small"
                    :columns="requestColumns"
                    :data-source="requests"
                    :row-key="(record, index) => index"
                    :loading="loading"
                    :pagination="{ pageSize: 15, size: 'small' }"
                    :locale="{ emptyText: 'Sem registro.' }"
                >
                    <template slot="request" slot-scope="text, order">{{ order.request.id }}</template>
                    <template slot="date" slot-scope="text, order">{{ order.request.created_at | formatDateOnly }}</template>
                </a-table>
            </div>

            <div class="phm-foot" v-if="!page">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </component>
</template>

<script>
import moment from "moment";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";
import OrderService from "@/services/v3/products/order.service";
import ProductService from "@/services/ProductService";
import ErrorHandlerService from "@/services/ErrorHandlerService";

export default {
    name: "ProductHistoryV2Modal",
    props: {
        product: { type: Object, required: true },
        brand: { type: Object, default: undefined },
        fromDate: { type: String, default: undefined },
        toDate: { type: String, default: undefined },
        page: { type: Boolean, default: false },
    },
    data() {
        return {
            ptBR,
            svc: new OrderService(),
            prod_svc: new ProductService(),
            tab: "offers",
            loading: false,
            rangeDate: this.fromDate && this.toDate ? [moment(this.fromDate), moment(this.toDate)] : [],
            brands: [],
            selectedBrandId: undefined,
            offers: [],
            requests: [],
            requestsLoaded: false,
            offersPagination: {
                current: 1,
                pageSize: 15,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            offerColumns: [
                { title: "Cotação", key: "request", scopedSlots: { customRender: "request" } },
                { title: "Empresa / Vendedor", key: "provider", scopedSlots: { customRender: "provider" } },
                { title: "Fat. Min.", key: "billing", align: "right", scopedSlots: { customRender: "billing" } },
                { title: "Preço", key: "price", align: "right", scopedSlots: { customRender: "price" } },
                { title: "Marca", key: "brand", scopedSlots: { customRender: "brand" } },
                { title: "Data", key: "date", align: "center", scopedSlots: { customRender: "date" } },
                { title: "Obs", dataIndex: "obs", key: "obs", scopedSlots: { customRender: "obs" } },
            ],
            requestColumns: [
                { title: "Cotação", key: "request", scopedSlots: { customRender: "request" } },
                { title: "Digitações", dataIndex: "offers_count", key: "offers_count", align: "center" },
                { title: "Pedidos", dataIndex: "orders_count", key: "orders_count", align: "center" },
                { title: "Data de Criação", key: "date", align: "center", scopedSlots: { customRender: "date" } },
            ],
        };
    },
    computed: {
        modalProps() {
            return {
                visible: true,
                width: "80vw",
                footer: null,
                closable: false,
                destroyOnClose: true,
                centered: true,
                wrapClassName: "phm-wrap",
            };
        },
        productId() {
            return this.product.pro_id || this.product.prod_id;
        },
        brandOptions() {
            return this.brands.map((b) => ({ value: b.pro_id || b.prod_id, label: b.pro_observacao }));
        },
    },
    created() {
        if (!this.brand) this.loadBrands();
        this.loadOffers(1);
    },
    methods: {
        filterOption(input, option) {
            const label = option.componentOptions.children[0].text || "";
            return label.toLowerCase().includes(input.toLowerCase());
        },
        onTabChange(tab) {
            if (tab === "requests" && !this.requestsLoaded) this.loadRequests();
        },
        rangeParams() {
            if (!this.rangeDate || this.rangeDate.length !== 2) return {};
            return {
                from: this.rangeDate[0].clone().startOf("day").toISOString(true),
                to: this.rangeDate[1].clone().endOf("day").toISOString(true),
            };
        },
        loadOffers(page) {
            this.loading = true;
            const { from, to } = this.rangeParams();
            const brand = this.brand ? this.brand.pro_id || this.brand.prod_id : this.selectedBrandId;
            this.svc
                .getProductHistory(this.productId, page, from, to, brand)
                .then(({ data }) => {
                    this.offers = data.data || [];
                    this.offersPagination.total = (data.meta && data.meta.total) || 0;
                    this.offersPagination.current = page;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadRequests() {
            this.loading = true;
            this.svc
                .getOrders(this.productId)
                .then(({ data }) => {
                    this.requests = (data || []).slice().sort((a, b) => b.request.id - a.request.id);
                    this.requestsLoaded = true;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadBrands() {
            this.prod_svc
                .get_product_brands(this.product.pro_id)
                .then(({ data }) => {
                    this.brands = (data.produtos || [])
                        .filter((b) => b.pro_observacao)
                        .sort((a, b) => a.pro_observacao.localeCompare(b.pro_observacao));
                })
                .catch(() => {});
        },
    },
};
</script>

<style lang="scss" scoped>
.phm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.phm-page {
        max-height: none;
        overflow: visible;

        .phm-tabs,
        .phm-tools {
            padding-left: 0;
            padding-right: 0;
        }

        .phm-body {
            overflow: visible;
            padding: 0;
            border-top: none;
        }
    }
}
.phm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 6px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.phm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.phm-x.ant-btn,
.phm-x.ant-btn:focus,
.phm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.phm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.phm-tabs {
    padding: 0 20px;

    ::v-deep .ant-tabs-bar {
        margin-bottom: 10px;
    }
}
.phm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.phm-range {
    width: 260px;
}
.phm-brand {
    flex: 1 1 200px;
    max-width: 300px;
}
.phm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.phm-strong {
    font-weight: 600;
    color: #262626;
    text-transform: uppercase;
}
.phm-muted {
    font-size: 12px;
    color: #8c8c8c;
    text-transform: uppercase;
}
.phm-money {
    white-space: nowrap;
}
.phm-purchased {
    padding: 2px 8px;
    border-radius: 6px;
    background: rgb(218, 244, 218);
    color: #30aa4c;
    font-weight: 600;
}
.phm-obs {
    display: inline-block;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}
.phm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.phm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
